import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EntityAvatar } from "@/components/catalogue/EntityAvatar";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage } from "@/lib/api";
import { agentName } from "@/lib/catalogue";

export const AccessDialog = ({ target, onClose, onDone }) => {
  const { teams, agents, workflows } = useCatalogue();
  const [mode, setMode] = useState("all");
  const [allowed, setAllowed] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!target) return;
    setMode(target.access?.mode || "all");
    setAllowed(target.access?.allowed || []);
  }, [target]);
  const entries = [...agents.filter((a) => !a.parent).map((a) => ({ id: a.emp_no, name: agentName(a), team: a.team, e: a, kind: "Agent" })),
    ...workflows.map((w) => ({ id: w.id, name: w.name, team: w.team, e: w, kind: "Workflow" }))];
  const toggle = (id, on) => setAllowed((s) => (on ? [...new Set([...s, id])] : s.filter((x) => x !== id)));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${target.id}/access`, { mode, allowed });
      toast.success(`Access saved for ${target.name}`);
      onDone();
      onClose();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-2xl" data-testid="access-dialog">
        <DialogHeader>
          <DialogTitle>Agent access for {target?.name}</DialogTitle>
          <DialogDescription>Choose which agents and workflows this person can see and run. Owners always see everything.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between rounded-xl border px-3 py-2.5">
          <Label htmlFor="access-all">Access to every agent and workflow</Label>
          <Switch id="access-all" checked={mode === "all"} onCheckedChange={(v) => setMode(v ? "all" : "selected")} data-testid="access-all-switch" />
        </div>
        {mode === "selected" && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={() => setAllowed(entries.map((x) => x.id))} data-testid="access-select-all">Turn all on</Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setAllowed([])} data-testid="access-clear">Turn all off</Button>
              <span className="ml-auto self-center text-sm text-muted-foreground">{allowed.length} of {entries.length} on</span>
            </div>
            {teams.map((t) => {
              const list = entries.filter((x) => x.team === t.id);
              if (!list.length) return null;
              return (
                <section key={t.id}>
                  <h3 className="mb-2 text-sm font-semibold">{t.section || t.name}</h3>
                  <ul className="space-y-1.5">
                    {list.map((x) => (
                      <li key={x.id} className="flex items-center gap-3 rounded-xl border px-3 py-2">
                        <EntityAvatar entity={x.e} name={x.name} size="sm" />
                        <label htmlFor={`access-${x.id}`} className="min-w-0 flex-1 text-sm"><span className="font-medium">{x.name}</span> <span className="text-muted-foreground">{x.kind} {x.id}</span></label>
                        <Switch id={`access-${x.id}`} checked={allowed.includes(x.id)} onCheckedChange={(v) => toggle(x.id, v)} data-testid={`access-toggle-${x.id}`} />
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="access-save">{busy ? "Saving" : "Save access"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
